import { CaseType, NextCasesTypesByCase, PasswordAttemptsCaseType } from './types';

export class PasswordAttemptManager {
  private originalPassword = '';

  private lastPassword = '';

  private repeatCount = 0;

  private caseType?: PasswordAttemptsCaseType;

  private isNewPassword(password: string): boolean {
    return this.originalPassword !== password;
  }

  private isSamePassword(password: string): boolean {
    return password.toLowerCase() === this.lastPassword.toLowerCase();
  }

  private resetState(password: string): void {
    this.originalPassword = password;
    this.lastPassword = password;
    this.repeatCount = 1;
    this.caseType = undefined;
  }

  setCurrentPassword(password: string): void {
    if (this.isNewPassword(password)) {
      this.resetState(password);
      return;
    }

    if (this.isSamePassword(password)) {
      this.repeatCount += 1;
      this.caseType = this.getCurrentCaseType(this.lastPassword);
    } else {
      this.lastPassword = password;
      this.repeatCount = 1;
    }
  }

  private getAllLowerCase(password: string): string {
    return password.toLowerCase();
  }

  private getAllUpperCase(password: string): string {
    return password.toUpperCase();
  }

  private getFirstUpperCase(password: string): string {
    return password.charAt(0).toUpperCase() + password.slice(1).toLowerCase();
  }

  private getInverted(password: string): string {
    return password
      .split('')
      .map(char => (char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()))
      .join('');
  }

  private getCurrentCaseType(password: string): PasswordAttemptsCaseType {
    if (password === password.toUpperCase()) {
      return PasswordAttemptsCaseType.ALL_UPPERCASE;
    }
    if (password === password.toLowerCase()) {
      return PasswordAttemptsCaseType.ALL_LOWERCASE;
    }
    if (
      password[0] === password[0].toUpperCase() &&
      password.slice(1) === password.slice(1).toLowerCase()
    ) {
      return PasswordAttemptsCaseType.FIRST_UPPERCASE;
    }
    return PasswordAttemptsCaseType.INVERTED;
  }

  private getCasesTypes(password: string): CaseType[] {
    if (password === password.toUpperCase()) {
      return NextCasesTypesByCase[PasswordAttemptsCaseType.ALL_UPPERCASE];
    }
    if (password === password.toLowerCase()) {
      return NextCasesTypesByCase[PasswordAttemptsCaseType.ALL_LOWERCASE];
    }
    if (
      password[0] === password[0].toUpperCase() &&
      password.slice(1) === password.slice(1).toLowerCase()
    ) {
      return NextCasesTypesByCase[PasswordAttemptsCaseType.FIRST_UPPERCASE];
    }
    return NextCasesTypesByCase[PasswordAttemptsCaseType.INVERTED];
  }

  getNextVariant(): string {
    if (this.repeatCount === 1 || !this.caseType) {
      return this.lastPassword;
    }
    if (this.repeatCount > 3) {
      return this.originalPassword;
    }

    const transformedPassword = this.applyCaseType(this.originalPassword);
    this.lastPassword = transformedPassword;
    return transformedPassword;
  }

  private getPasswordByCaseType(password: string): string {
    const caseType = this.getCasesTypes(password);
    const returnCase = caseType.find(item => item.id === this.repeatCount);
    if (returnCase?.type === PasswordAttemptsCaseType.ALL_UPPERCASE) {
      return this.getAllUpperCase(password);
    }
    if (returnCase?.type === PasswordAttemptsCaseType.ALL_LOWERCASE) {
      return this.getAllLowerCase(password);
    }
    if (returnCase?.type === PasswordAttemptsCaseType.FIRST_UPPERCASE) {
      return this.getFirstUpperCase(password);
    }
    if (returnCase?.type === PasswordAttemptsCaseType.INVERTED) {
      return this.getInverted(password);
    }
    return password;
  }

  private applyCaseType(password: string): string {
    return this.getPasswordByCaseType(password);
  }
}
